import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/message-dialog.module.css";
import useCustomInput from "@/hooks/use-custom-input";
import * as signals from "@/signals";
import { Dialog, DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { FormEvent, JSX, useId, useState } from "react";

interface AddCustomSearchEnginePopupProps {
	isShown: boolean;
	setIsShown: (newValue: boolean) => void;
}

function AddCustomSearchEnginePopup({
	isShown,
	setIsShown,
}: AddCustomSearchEnginePopupProps): JSX.Element {
	const queryClient = useQueryClient();

	const nameInputId = useId();
	const urlInputId = useId();

	const [isClosing, setIsClosing] = useState<boolean>(false);
	const [nameInputValue, setNameInputValue] = useState<string>("");
	const [urlInputValue, setUrlInputValue] = useState<string>("");

	const nameInput = useCustomInput<HTMLInputElement>({
		setValue: setNameInputValue,
	});
	const urlInput = useCustomInput<HTMLInputElement>({
		setValue: setUrlInputValue,
	});

	const isRequirementsHighlighted =
		!!urlInputValue &&
		!(urlInputValue.startsWith("https://") && urlInputValue.includes("%s"));

	const createSearchEngine = async (
		event: FormEvent<HTMLFormElement>,
	): Promise<void> => {
		event.preventDefault();
		if (!nameInputValue || !urlInputValue || isRequirementsHighlighted) {
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).createSearchEngine(
				nameInputValue,
				urlInputValue,
			);
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
			handleClose();
		}
	};

	const handleClose = (): void => {
		setUrlInputValue("");
		setNameInputValue("");
		setIsClosing(true);
		setTimeout(() => {
			setIsShown(false);
			setIsClosing(false);
		}, 250);
	};

	return (
		<Dialog
			onClose={handleClose}
			open={isShown}
		>
			<div
				className={clsx(
					"overlay",
					"z-index", // above popup
					!isClosing && "zoom-in",
					isClosing && "zoom-out",
					styles["dialog-overlay"],
				)}
			>
				<form
					className={styles["dialog-container"]}
					onSubmit={(event) => void createSearchEngine(event)}
				>
					<div
						className={clsx(
							styles["prompt-body"],
							styles["multiple-fields"],
						)}
					>
						<DialogTitle className={styles["dialog-title"]}>
							{t("addCustomSearchEngine")}
						</DialogTitle>
						<label htmlFor={urlInputId}>
							URL
							<div className={styles["label-description"]}>
								{t("searchUrlOfSearchEngine")}
								<span
									className={clsx(
										isRequirementsHighlighted &&
											styles["highlighted"],
									)}
								>
									{t("customSearchEngineUrlRequirements")}
								</span>
							</div>
						</label>
						<input
							autoComplete="off"
							className="input"
							data-autofocus
							id={urlInputId}
							inputMode="url"
							type="url"
							value={urlInputValue}
							{...urlInput}
						/>
						<label htmlFor={nameInputId}>{t("name")}</label>
						<input
							autoComplete="off"
							className="input"
							id={nameInputId}
							type="text"
							value={nameInputValue}
							{...nameInput}
						/>
					</div>
					<div className={styles["dialog-buttons"]}>
						<button
							className={styles["dialog-button"]}
							onClick={handleClose}
							type="button"
						>
							{t("cancel")}
						</button>
						<button
							className={clsx(
								styles["dialog-button"],
								styles["default"],
							)}
							type="submit"
						>
							{t("confirmButton")}
						</button>
					</div>
				</form>
			</div>
		</Dialog>
	);
}

export default AddCustomSearchEnginePopup;
